import { useQuery, useMutation, useQueryClient } from "react-query";
import {
	getLookupsSpecieVariety,
	getSpecieVariety,
	getSpecieVarieties,
	saveSpecieVariety,
	deleteSpecieVariety,
	importExcelSpecieVariety,
} from "@/api/Maintainers/BusinessCenter/SpecieVariety/specie-variety-api";

export const key = "specie-variety";

export function useSpecieVarietyLookups() {
	return useQuery([key, "lookups"], () => getLookupsSpecieVariety());
}

export function useSpecieVarieties(filters) {
	return useQuery([key, "list", filters], () => getSpecieVarieties(filters));
}

export function useSpecieVariety(filters, elementId, canLoadDetail) {
	return useQuery([key, "detail", elementId], () => getSpecieVariety({ ...filters, id: elementId }), {
		enabled: canLoadDetail,
	});
}

export function useSaveSpecieVariety() {
	const queryClient = useQueryClient();

	return useMutation(saveSpecieVariety, {
		onMutate: async (variables) => {
			const filters = variables.filters;

			await queryClient.cancelQueries([key, "list", filters]);

			const previousData = queryClient.getQueryData([key, "list", filters]);

			queryClient.setQueryData([key, "list", filters], (old) => {
				if (!old) return old;

				const updatedData = old.data.map((item) => {
					if (item.id === variables.form.id) {
						return {
							...item,
							specie: variables.form.specie_object || item.specie,
							varieties: variables.form.varieties_objects || item.varieties,
							estates_parents: variables.form.estates_parents_objects || item.estates_parents,
						};
					}
					return item;
				});

				return {
					...old,
					data: updatedData,
				};
			});

			return { previousData, filters };
		},
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;
			const detail = response.detail;

			queryClient.setQueryData([key, "detail", detail.id], detail);
			queryClient.setQueryData([key, "list", filters], list);

			return response;
		},
		onError: (error, variables, context) => {
			if (context?.previousData) {
				queryClient.setQueryData([key, "list", context.filters], context.previousData);
			}
		},
	});
}

export function useUpdateSpecieVarietyLookups() {
	const queryClient = useQueryClient();

	const addCropToLookups = (newCrop) => {
		queryClient.setQueryData([key, "lookups"], (oldData) => {
			if (!oldData) return oldData;

			return {
				...oldData,
				crops: [...(oldData.crops || []), newCrop],
			};
		});
	};

	const addEstateToLookups = (newEstate) => {
		queryClient.setQueryData([key, "lookups"], (oldData) => {
			if (!oldData) return oldData;

			return {
				...oldData,
				estates: [...(oldData.estates || []), newEstate],
			};
		});
	};

	return {
		addCropToLookups,
		addEstateToLookups,
	};
}

export function useDeleteSpecieVariety() {
	const queryClient = useQueryClient();

	return useMutation(deleteSpecieVariety, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;

			queryClient.setQueryData([key, "list", filters], list);
		},
	});
}

export function useImportExcelSpecieVariety() {
	const queryClient = useQueryClient();

	return useMutation(importExcelSpecieVariety, {
		onSuccess: (response) => {
			queryClient.invalidateQueries([key, "list"]);

			if (response?.should_refresh_lookups) {
				queryClient.invalidateQueries([key, "lookups"]);
			}
		},
	});
}
